import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, Snackbar, Alert, Divider } from '@mui/material';
import { getLegalSettings, saveLegalSettings } from '../components/services/consentService';
import { useLegalAuth as useAuth } from '../hooks/useLegalAuth';

export default function LegalSettingsAdmin() {
  const { currentUser } = useAuth();
  const [tncContent, setTncContent] = useState('');
  const [privacyPolicyContent, setPrivacyPolicyContent] = useState('');
  const [thirdPartyLicensingContent, setThirdPartyLicensingContent] = useState('');
  const [additionalRemarks, setAdditionalRemarks] = useState('-');
  const [currentVersion, setCurrentVersion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    getLegalSettings().then((s) => {
      setTncContent(s.tncContent || '');
      setPrivacyPolicyContent(s.privacyPolicyContent || '');
      setThirdPartyLicensingContent(s.thirdPartyLicensingContent || '');
      setAdditionalRemarks(s.additionalRemarks || '-');
      setCurrentVersion(s.tncVersion);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveLegalSettings({
        tncContent,
        privacyPolicyContent,
        thirdPartyLicensingContent,
        additionalRemarks,
        updatedByEmail: currentUser?.email,
      });
      setCurrentVersion(result.tncVersion);
      setToast({
        severity: 'success',
        message: result.tncChanged
          ? `Saved. T&C content changed — version bumped to ${result.tncVersion}. All users will be asked to re-accept on next login.`
          : 'Saved. No change to T&C text, so no re-consent will be triggered.',
      });
    } catch (err) {
      console.error(err);
      setToast({ severity: 'error', message: 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Legal Settings</Typography>
      <Typography sx={{ color: '#6B6480', mb: 3 }}>
        Current T&C version: {currentVersion ?? '—'}. Editing the Terms & Conditions text below will bump the
        version and require every user to re-accept on their next login. Editing Privacy Policy or
        Third-Party Licensing alone does not trigger re-consent.
      </Typography>

      <Typography sx={{ fontWeight: 700, mb: 1 }}>Terms & Conditions</Typography>
      <TextField
        fullWidth multiline minRows={12} value={tncContent}
        onChange={(e) => setTncContent(e.target.value)}
        sx={{ mb: 3, background: '#fff' }}
      />

      <Typography sx={{ fontWeight: 700, mb: 1 }}>Privacy Policy</Typography>
      <TextField
        fullWidth multiline minRows={10} value={privacyPolicyContent}
        onChange={(e) => setPrivacyPolicyContent(e.target.value)}
        sx={{ mb: 3, background: '#fff' }}
      />

      <Typography sx={{ fontWeight: 700, mb: 1 }}>Third-Party Software & Licensing</Typography>
      <TextField
        fullWidth multiline minRows={10} value={thirdPartyLicensingContent}
        onChange={(e) => setThirdPartyLicensingContent(e.target.value)}
        sx={{ mb: 3, background: '#fff' }}
      />

      <Divider sx={{ my: 3 }} />

      <Typography sx={{ fontWeight: 700, mb: 1 }}>Additional Remarks</Typography>
      <TextField
        fullWidth value={additionalRemarks}
        onChange={(e) => setAdditionalRemarks(e.target.value)}
        placeholder="-"
        sx={{ mb: 3, background: '#fff' }}
      />

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving}
        sx={{ textTransform: 'none', fontWeight: 700, px: 4, py: 1.2 }}
      >
        {saving ? 'Saving...' : 'Save'}
      </Button>

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}>
        {toast && <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert>}
      </Snackbar>
    </Box>
  );
}